import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "step", "stepper", "nextBtn", "prevBtn", "submitBtn",
    "spouseTemplate", "childTemplate", "spouseContainer", "childContainer",
    "identityRadio", "identityHidden", "addSpouseBtn", "form"
  ]

  static values = {
    currentStep: { type: Number, default: 0 },
    totalSteps: Number,
    maxSpouses: { type: Number, default: 2 },
    maxChildren: { type: Number, default: 10 },
    networkError: String
  }

  connect() {
    this.spouseCount = 0
    this.childCount = 0
    this.showStep(this.currentStepValue)
    this.boundNetworkError = this.handleNetworkError.bind(this)
    document.addEventListener("turbo:fetch-request-error", this.boundNetworkError)
  }

  disconnect() {
    this.spouseCount = 0
    this.childCount = 0
    document.removeEventListener("turbo:fetch-request-error", this.boundNetworkError)
  }

  next() {
    if (this.currentStepValue >= this.totalStepsValue - 1) return
    this.currentStepValue++
    this.showStep(this.currentStepValue)
  }

  previous() {
    if (this.currentStepValue <= 0) return
    this.currentStepValue--
    this.showStep(this.currentStepValue)
  }

  showStep(index) {
    this.stepTargets.forEach((el, i) => {
      el.hidden = i !== index
    })
    this.updateStepper(index)
    this.focusFirstInput(index)
  }

  updateStepper(index) {
    this.stepperTargets.forEach((el, i) => {
      if (i === index) {
        el.setAttribute("aria-current", "step")
        el.classList.add("bg-emerald-600", "text-white")
        el.classList.remove("bg-white/10", "text-muted")
      } else {
        el.removeAttribute("aria-current")
        el.classList.remove("bg-emerald-600", "text-white")
        el.classList.add("bg-white/10", "text-muted")
      }
    })
  }

  focusFirstInput(index) {
    const step = this.stepTargets[index]
    if (!step) return
    const input = step.querySelector("input:not([type=hidden]):not([type=radio]):not([type=checkbox])")
    if (input) requestAnimationFrame(() => input.focus())
  }

  addSpouse() {
    if (this.spouseCount >= this.maxSpousesValue) return
    const clone = this.cloneTemplate(this.spouseTemplateTarget, this.spouseCount)
    this.spouseContainerTarget.appendChild(clone)
    this.spouseCount++
    this.toggleAddSpouseBtn()
    this.bindSpouseNameTracking()
  }

  addChild() {
    if (this.childCount >= this.maxChildrenValue) return
    const clone = this.cloneTemplate(this.childTemplateTarget, this.childCount)
    this.assignChildIdentity(clone, this.childCount)
    this.updateSpouseSelectors(clone)
    this.childContainerTarget.appendChild(clone)
    this.childCount++
  }

  cloneTemplate(template, index) {
    const fragment = template.content.cloneNode(true)
    fragment.querySelectorAll("[data-name]").forEach(el => {
      el.setAttribute("name", el.dataset.name.replace("[]", `[${index}]`))
      el.removeAttribute("data-name")
    })
    return fragment
  }

  assignChildIdentity(fragment, index) {
    const radio = fragment.querySelector("[data-wizard-target='identityRadio']")
    if (radio) radio.value = index.toString()
  }

  toggleAddSpouseBtn() {
    if (!this.hasAddSpouseBtnTarget) return
    this.addSpouseBtnTarget.hidden = this.spouseCount >= this.maxSpousesValue
  }

  selectIdentity() {
    // Radio buttons handle mutual exclusion natively
  }

  bindSpouseNameTracking() {
    this.spouseContainerTarget.querySelectorAll("[data-spouse-name]").forEach(fieldset => {
      const firstNameInput = fieldset.querySelector("[name*='first_name']")
      if (!firstNameInput || firstNameInput.dataset.tracked) return
      firstNameInput.dataset.tracked = "true"
      firstNameInput.addEventListener("input", () => {
        fieldset.dataset.spouseName = firstNameInput.value
      })
    })
  }

  updateSpouseSelectors(fragment) {
    const select = fragment.querySelector("[data-spouse-select]")
    if (!select) return
    const spouses = this.spouseContainerTarget.querySelectorAll("[data-spouse-name]")
    if (spouses.length <= 1) {
      select.closest("[data-spouse-selector]").hidden = true
      return
    }
    select.innerHTML = ""
    spouses.forEach((el, i) => {
      const option = document.createElement("option")
      option.value = i.toString()
      option.textContent = el.dataset.spouseName || `${i + 1}`
      select.appendChild(option)
    })
  }

  handleNetworkError(event) {
    event.preventDefault()
    const msg = this.networkErrorValue || "Connexion perdue — réessayez"
    alert(msg)
  }
}
